import { useId } from "react";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { TextField } from "../design/components/TextField";
import { messageForError } from "../i18n/errors";
import { formatNumericDate, localDayOf } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import { SEARCH_MAX_LENGTH } from "../leads/lead-list-hooks";
import { useAccess } from "../session/access";
import { DEFAULT_PERIOD, PERIOD_PARAMS, type ConversationsSearch, type PeriodParam } from "./conversation-search";

// I pezzi comuni alle due schede di «Conversazioni» (piano M4, Task 19; piano M6, R3, passo 12b): ciò che una scheda riceve dalla
// pagina, il periodo, il campo della ricerca e il problema di una lettura con «Riprova».
//
// Il periodo (SEZ-3, più «Tutte»): giorni interi nel fuso dell'azienda, con i giorni scritti sotto, così «Settimana» e «Mese» non
// lasciano dubbi («Dal 01/10/2026 al 07/10/2026»: oggi e i 6 giorni prima; il «Mese» oggi e i 29 giorni prima, decisione del 28
// settembre 2026). «Personalizzato» parte da oggi a oggi e chiede «Dal» e «Al»: un «Dal» dopo «Al» porta con sé «Al», e un «Al»
// prima di «Dal» porta con sé «Dal», così il periodo resta sempre in ordine. In un elenco il confronto col periodo precedente non
// c'è: è dei numeri di Home e Report.

/** Ciò che una scheda riceve dalla pagina: l'indirizzo letto, come cambiarlo e come scriverci la ricerca. */
export type ConversationsTabProps = {
  search: ConversationsSearch;
  update: (change: Partial<ConversationsSearch>) => void;
  writeQ: (q: string | undefined) => void;
};

/** I periodi, nell'ordine di SEZ-3, e «Tutte»; «Settimana» è quello di partenza. */
const PERIODS: readonly PeriodParam[] = ["oggi", "ieri", "settimana", "mese", "personalizzato", "tutte"];

function periodOptionsOf(t: Translate) {
  return PERIODS.map((id) => ({ value: id, label: t(`conversations.periods.${PERIOD_PARAMS[id]}`) }));
}

/** Il periodo scelto per l'indirizzo: quello di partenza non si scrive. */
function periodParamOf(value: string): Exclude<PeriodParam, typeof DEFAULT_PERIOD> | undefined {
  return PERIODS.find((id): id is Exclude<PeriodParam, typeof DEFAULT_PERIOD> => id === value && id !== DEFAULT_PERIOD);
}

/** Il giorno `AAAA-MM-GG` spostato di `days` giorni di calendario (un giorno di calendario non ha fuso). */
function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Il primo e l'ultimo giorno di un periodo pronto, contando da `today`; nessuno per «Tutte» e «Personalizzato». */
function periodDays(period: PeriodParam, today: string): { from: string; to: string } | null {
  switch (period) {
    case "oggi":
      return { from: today, to: today };
    case "ieri":
      return { from: shiftDay(today, -1), to: shiftDay(today, -1) };
    case "settimana":
      return { from: shiftDay(today, -6), to: today };
    case "mese":
      return { from: shiftDay(today, -29), to: today };
    default:
      return null;
  }
}

/** Un giorno scritto in un campo data: `AAAA-MM-GG` negli anni che il server accetta; un campo vuoto o a metà no. */
const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= "1900-01-01" && value <= "9998-12-31";

/** Il periodo delle due schede: i pulsanti, i giorni del periodo scelto e, con «Personalizzato», «Dal» e «Al». */
export function ConversationsPeriod({ search, update }: Pick<ConversationsTabProps, "search" | "update">) {
  const t = useT();
  const baseId = useId();
  const timeZone = useAccess().context.settings.timeZone;
  const today = localDayOf(timeZone, new Date().toISOString());
  const period = search.periodo ?? DEFAULT_PERIOD;
  const days = periodDays(period, today);

  function choose(value: string) {
    const periodo = periodParamOf(value);
    if (periodo === "personalizzato") update({ periodo, dal: search.dal ?? today, al: search.al ?? today });
    else update({ periodo, dal: undefined, al: undefined });
  }

  function changeFrom(value: string) {
    if (!isDay(value)) return;
    const al = search.al ?? today;
    update({ dal: value, al: value > al ? value : al });
  }

  function changeTo(value: string) {
    if (!isDay(value)) return;
    const dal = search.dal ?? today;
    update({ al: value, dal: value < dal ? value : dal });
  }

  let detail = null;
  if (period === "personalizzato") {
    detail = (
      <div className="g-conversations__days">
        <TextField id={`${baseId}-from`} label={t("conversations.fromDay")} type="date" min="1900-01-01" max="9998-12-31" value={search.dal ?? today} onChange={(event) => changeFrom(event.target.value)} />
        <TextField id={`${baseId}-to`} label={t("conversations.toDay")} type="date" min="1900-01-01" max="9998-12-31" value={search.al ?? today} onChange={(event) => changeTo(event.target.value)} />
      </div>
    );
  } else if (days) {
    const from = formatNumericDate(timeZone, days.from);
    detail = <p className="g-conversations__period-days">{days.from === days.to ? t("conversations.periodDay", { day: from }) : t("conversations.periodDays", { from, to: formatNumericDate(timeZone, days.to) })}</p>;
  }

  return (
    <div className="g-conversations__period">
      <Segmented label={t("conversations.periodLabel")} options={periodOptionsOf(t)} value={period} onChange={choose} />
      {detail}
    </div>
  );
}

/** «Cerca un Lead o un salone», lo stesso campo per le due schede. */
export function ConversationsSearchField({ id, text, onChange }: { id: string; text: string; onChange: (text: string) => void }) {
  const t = useT();
  return (
    <div className="g-lead-search g-conversations__search">
      <Icon name="search" className="g-lead-search__icon" />
      <label className="g-visually-hidden" htmlFor={id}>
        {t("conversations.search")}
      </label>
      <input
        id={id}
        className="g-field__input"
        type="search"
        value={text}
        maxLength={SEARCH_MAX_LENGTH}
        autoComplete="off"
        placeholder={t("conversations.search")}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** Una lettura non riuscita: il perché e «Riprova». */
export function ConversationsProblem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  return (
    <div className="g-conversations__problem">
      <Reason tone="warning" role="alert">
        {messageForError(t, error)}
      </Reason>
      <Button onClick={onRetry}>{t("common.retry")}</Button>
    </div>
  );
}
